/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/no-runloop, ember/require-computed-property-dependencies */
// Standard Library Imports
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { get, set, action, computed } from '@ember/object';
import { debounce } from '@ember/runloop';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';

// External Imports
import moment from 'moment';
import DataType from '@linc-technologies/linced-common/enums/data-type';

// Internal Imports
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import { observes } from '@ember-decorators/object';

// Constant Values
const defaultSortDir = 'desc';
const defaultSortProp = 'createTime';

export default class ChangeOfAttendanceRecordController extends Controller {
  @action
  setLimit(value) {
    this.limit = value;
  }

  @action
  setPage(value) {
    this.page = value;
  }

  // Attrs
  // timestampFormat provides a human friendly date format for the create
  // timestamp.
  timestampFormat = 'll [at] LT';

  // queryParams provides a binding to the routes query params.
  queryParams = ['dateFrom', 'dateTo', 'termId'];

  // limitOptions provides the pagination page limit.
  limitOptions = [5, 10, 20, 50, 100];

  // Services
  @service
  attendanceCodes;

  @service
  session;

  @service
  dates;

  @service
  excel;

  // State
  // dateFrom provides the stateful binding to a query param.
  dateFrom = '';

  // newDateFrom enables the user to enter a date without it immediately updating query params
  // as soon as the date appears "valid", i.e. when the first digit of the year is entered.
  @tracked
  newDateFrom = '';

  // dateTo provides the stateful binding to a query param.
  dateTo = '';

  // newDateTo enables the user to enter a date without it immediately updating query params
  // as soon as the date appears "valid", i.e. when the first digit of the year is entered.
  @tracked
  newDateTo = '';

  // filters contains data relating to the table rows that are filterable.
  @tracked
  filters = [];

  // limit provides the number of audit logs to show at once.
  @tracked limit = 10;

  // page the page of data to start on for pagination. 1 indexed.
  @tracked page = 1;

  // sortDir provides the current direction of sorting.
  @tracked sortDir = defaultSortDir;

  // sortProp provides the current entity to be used to sort by.
  @tracked sortProp = defaultSortProp;

  // termId provides the stateful binding to a query param.
  @tracked termId = '';

  // Methods
  // filteredData applies the user's filters to the data array computed above.
  @computed('model.audits.[]', 'filters.@each.{field,value}')
  get filteredData() {
    if (isEmpty(this.filters)) {
      // No filters - just return the data.
      return this.model.audits;
    }

    // Map raw filters into what we need to actually perform the filtering.
    let filters = this.filters.map((filter) => {
      let dataType = DataType.STRING;
      let key = filter.field;
      let value = filter.value;

      switch (filter.field) {
        case 'newAuthor.heroId':
        case 'record.person.heroId':
          dataType = DataType.INTEGER;
          break;
      }

      return {
        key,
        dataType,
        value,
      };
    });

    // Apply all filters to each row in one hit, to avoid iterating over a
    // (potentially) large dataset multiple times.
    return this.model.audits.filter((row) => {
      return filters.every((filter) => {
        let cellValue = get(row, filter.key) ?? '';
        if (filter.key === 'createTime') {
          // We do some magic when grabbing the create time from the row data,
          // transforming it using moment into the same display string the user
          // sees in order to filter values in a human friendly manner.
          // Therefore we don't need to mark createTime's datatype as we would
          // above, as the transformed datatype for value is a string.
          cellValue = moment.unix(cellValue).format(this.timestampFormat);
        }

        return filterValue(cellValue, filter.dataType, filter.value);
      });
    });
  }

  // pages provides a number of pages based on the sorted filtered data.
  @computed('limit', 'sortedFilteredAudits.[]')
  get pages() {
    let max = Math.max(
      1,
      Math.ceil(this.get('sortedFilteredAudits.length') / this.limit),
    );
    let pages = [];
    for (let i = 1; i <= max; i++) {
      pages.push(i);
    }
    return pages;
  }

  // reset removes all filters.
  reset() {
    this.filters = [];
    this.sortDir = defaultSortDir;
    this.sortProp = defaultSortProp;
  }

  // selectedTerm returns the currently selected term.
  @computed('termId', 'model.terms.[]')
  get selectedTerm() {
    return this.model.terms.find((item) => item.id === this.termId);
  }

  // sortedFilteredAudits sorts the filtered audit records by the chosen
  // property and direction.
  @computed('filteredData.[]', 'sortDir', 'sortProp')
  get sortedFilteredAudits() {
    let sortedAudits = this.filteredData
      .slice()
      .sort((a, b) =>
        a[this.sortProp] > b[this.sortProp]
          ? 1
          : a[this.sortProp] < b[this.sortProp]
            ? -1
            : 0,
      );

    return this.sortDir === 'asc' ? sortedAudits : sortedAudits.reverse();
  }

  @computed('page', 'limit', 'sortProp', 'sortDir', 'sortedFilteredAudits.[]')
  get paginatedAudits() {
    let ind = (this.page - 1) * this.limit;
    return this.sortedFilteredAudits.slice().splice(ind, this.limit);
  }

  // _datesChanged updates the "new" date properties to keep them in line with the query params.
  @observes('dateFrom', 'dateTo')
  _datesChanged() {
    this.newDateFrom = this.dateFrom;
    this.newDateTo = this.dateTo;
  }

  // updateDates should be run debounced in order to prevent the route's model reloading
  // as soon as a "valid" date is entered, as the first digit of the year will make it "valid".
  updateDates() {
    let props = {
      termId: '',
    };
    let hasNewValue = false;

    if (!isEmpty(this.newDateFrom)) {
      props.dateFrom = this.newDateFrom;
      props.newDateFrom = null;
      hasNewValue = true;
    }

    if (!isEmpty(this.newDateTo)) {
      props.dateTo = this.newDateTo;
      props.newDateTo = null;
      hasNewValue = true;
    }

    if (hasNewValue) {
      this.setProperties(props);
    }
  }

  /**
   * decrementPage moves to the previous page of data.
   */
  @action
  decrementPage() {
    let page = this.page;
    if (page > 1) {
      this.page = page - 1;
    }
  }

  /**
   * filter provides a way to dynamically add and remove filters against the
   * dataset as the user enters more filter data.
   *
   * @param {Object} field - field provides a dynamic field.
   * @param {string} value - The filter value to filter the records by.
   */
  @action
  filter(field, value) {
    let filter = this.filters.find((item) => item.field === field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      this.filters = [...this.filters, { field, value }];
      return;
    }

    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this.filters = this.filters.filter((f) => f !== filter);
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }

  /**
   * incrementPage moves to the next page of data.
   */
  @action
  incrementPage() {
    let page = this.page;
    let max = this.pages.reduce((prev, curr) => (curr > prev ? curr : prev), 0);
    if (page < max) {
      this.page = page + 1;
    }
  }

  /**
   * onChangeDateFrom changes the newDateFrom field starts the debounce timer
   * to push this change to dateFrom and clear the currently set term (if any).
   *
   * @param {string} newDate - The new value of the date input field.
   */
  @action
  onChangeDateFrom(newDate) {
    this.newDateFrom = newDate;
    debounce(this, 'updateDates', 500);
  }

  /**
   * onChangeDateTo changes the newDateTo field starts the debounce timer
   * to push this change to dateTo and clear the currently set term (if any).
   *
   * @param {string} newDate - The new value of the date input field.
   */
  @action
  onChangeDateTo(newDate) {
    this.newDateTo = newDate;
    debounce(this, 'updateDates', 500);
  }

  /**
   * onChangeSort binds in the new parameters for sorting based on what the
   * user chooses.
   *
   * @param {string} sortProp - The field to be sorted by.
   * @param {string} sortDir - The direction to sort in {'asc' | 'desc'}.
   */
  @action
  onChangeSort({ sortProp, sortDir }) {
    this.sortDir = sortDir;
    this.sortProp = sortProp;
  }

  /**
   * setSelectedTermId sets the query params, which in turn will refresh the
   * model.
   *
   * @param {Object} term - A term record.
   */
  @action
  setSelectedTermId(term) {
    this.setProperties({
      dateFrom: term.start,
      dateTo: term.end,
      termId: term.id,
    });
  }
}
